import { useEffect, useState } from "react";
import api, { fileUrl, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Check, DoorOpen, DoorClosed, Lock, SlidersHorizontal, Plus, Trash2, Save, RotateCcw } from "lucide-react";

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.is_super === true;
  const showForm = !isAdmin || canEdit;
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({});
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [empId, setEmpId] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // manage (super admin)
  const [mngEmp, setMngEmp] = useState("");
  const [overrideVal, setOverrideVal] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [manualDate, setManualDate] = useState(today);
  const [addingDate, setAddingDate] = useState(false);

  const load = () => {
    const q = isAdmin ? "" : `?employee_id=${user.employee_id}`;
    api.get(`/attendance${q}`).then((r) => setRecords(r.data));
    if (isAdmin) api.get("/assessments").then((r) => {
      const m = {}; r.data.forEach((a) => { m[a.employee_id] = a.attendance_count; }); setCounts(m);
    });
  };
  const loadStatus = () => api.get("/attendance/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data));
    else setEmpId(user.employee_id);
    load();
    loadStatus();
    // eslint-disable-next-line
  }, []);

  const isOpen = status?.is_open === true;

  const toggleStatus = async () => {
    setTogglingStatus(true);
    try {
      const { data } = await api.post(isOpen ? "/attendance/close" : "/attendance/open");
      setStatus(data);
      toast.success(data.is_open ? "Presensi hari ini DIBUKA" : "Presensi hari ini DITUTUP");
    } catch (err) { toast.error(errText(err)); } finally { setTogglingStatus(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/attendance", { employee_id: isAdmin ? empId : user.employee_id, date: today });
      toast.success("Kehadiran berhasil dicatat");
      load();
    } catch (err) { toast.error(errText(err)); } finally { setSaving(false); }
  };

  const saveOverride = async () => {
    if (!mngEmp) { toast.error("Pilih karyawan dulu"); return; }
    setSavingOverride(true);
    try {
      const count = overrideVal === "" ? null : parseInt(overrideVal, 10);
      await api.put(`/employees/${mngEmp}/attendance-count`, { count });
      toast.success(count === null ? "Dikembalikan ke perhitungan otomatis" : `Jumlah presensi diset ke ${count}`);
      load();
    } catch (err) { toast.error(errText(err)); } finally { setSavingOverride(false); }
  };

  const addManualDate = async () => {
    if (!mngEmp) { toast.error("Pilih karyawan dulu"); return; }
    setAddingDate(true);
    try {
      await api.post("/attendance/manual", { employee_id: mngEmp, date: manualDate });
      toast.success(`Presensi ${manualDate} ditambahkan`);
      load();
    } catch (err) { toast.error(errText(err)); } finally { setAddingDate(false); }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Hapus catatan presensi ini?")) return;
    try {
      await api.delete(`/attendance/${id}`);
      toast.success("Catatan presensi dihapus");
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const mngRecords = records.filter((r) => r.employee_id === mngEmp).sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Pencatatan Kehadiran</h1>
        <p className="mt-1 text-slate-500">{isAdmin ? (canEdit ? "Buka sesi presensi, catat & kelola kehadiran karyawan." : "Riwayat kehadiran karyawan.") : "Tandai kehadiran Anda hari ini."}</p>
      </div>

      {/* Status presensi */}
      <div data-testid="attendance-status-card" className={`rounded-2xl border p-6 shadow-sm ${isOpen ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${isOpen ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
              {isOpen ? <DoorOpen className="h-6 w-6" /> : <DoorClosed className="h-6 w-6" />}
            </div>
            <div>
              <p className="font-heading font-bold text-slate-900">Status Presensi Hari Ini</p>
              <p className="text-sm text-slate-500">{today} · {status === null ? "memuat…" : (isOpen ? <span className="font-semibold text-emerald-600">TERBUKA</span> : <span className="font-semibold text-slate-500">TERTUTUP</span>)}</p>
            </div>
          </div>
          {canEdit && (
            <button data-testid="toggle-attendance-status" onClick={toggleStatus} disabled={togglingStatus || status === null}
              className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${isOpen ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {togglingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : (isOpen ? <DoorClosed className="h-4 w-4" /> : <DoorOpen className="h-4 w-4" />)}
              {isOpen ? "Tutup Presensi" : "Buka Presensi Hari Ini"}
            </button>
          )}
        </div>
        {!isOpen && !canEdit && (
          <p className="mt-3 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-medium text-slate-500"><Lock className="h-3.5 w-3.5" /> Presensi belum dibuka oleh admin. Mohon tunggu sesi dibuka.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {showForm && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm h-fit">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><CalendarCheck className="h-5 w-5" /></div>
            <h3 className="font-heading font-bold text-slate-900">Form Presensi</h3>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {isAdmin && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Karyawan</label>
                <select data-testid="attendance-employee-select" required value={empId} onChange={(e) => setEmpId(e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                  <option value="">Pilih karyawan</option>
                  {employees.map((e) => <option key={e.id} value={e.employee_id}>{e.employee_id} — {e.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tanggal Kehadiran</label>
              <input data-testid="attendance-date-input" type="date" value={today} readOnly className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 outline-none cursor-not-allowed" />
              <p className="mt-1.5 text-xs text-slate-400">Presensi hanya dapat diisi untuk hari ini saat sesi dibuka.</p>
            </div>
            <button data-testid="attendance-submit-button" type="submit" disabled={saving || !isOpen} className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isOpen ? <Check className="h-4 w-4" /> : <Lock className="h-4 w-4" />} {isOpen ? "Catat Kehadiran" : "Presensi Ditutup"}
            </button>
          </form>
        </div>
        )}

        <div className={`${showForm ? "lg:col-span-2" : "lg:col-span-3"} rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden`}>
          <div className="p-6 pb-3">
            <h3 className="font-heading font-bold text-slate-900">Riwayat Kehadiran</h3>
            <p className="text-sm text-slate-500">{records.length} catatan</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-6 py-3 font-semibold">Karyawan</th>
                  <th className="px-6 py-3 font-semibold">ID</th>
                  <th className="px-6 py-3 font-semibold">Tanggal</th>
                  {canEdit && <th className="px-6 py-3 font-semibold text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && (
                  <tr><td colSpan={canEdit ? 4 : 3} className="px-6 py-10 text-center text-slate-400">Belum ada catatan kehadiran.</td></tr>
                )}
                {records.map((r) => (
                  <tr key={r.id} data-testid={`attendance-row-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50 transition">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        {r.photo_url ? <img src={fileUrl(r.photo_url)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{r.employee_name[0]}</div>}
                        <span className="font-medium text-slate-800">{r.employee_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 font-mono text-xs text-slate-500">{r.employee_id}</td>
                    <td className="px-6 py-3 text-slate-600">{r.date}</td>
                    {canEdit && (
                      <td className="px-6 py-3 text-right">
                        <button data-testid={`delete-attendance-${r.id}`} onClick={() => deleteRecord(r.id)} className="text-slate-400 hover:text-rose-600 transition"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Kelola jumlah presensi (super admin) */}
      {canEdit && (
        <div data-testid="manage-attendance-card" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-[#F16522]"><SlidersHorizontal className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Kelola Jumlah Presensi (Manual)</h3>
          </div>
          <p className="mb-4 text-sm text-slate-500">Set angka total presensi secara manual (override untuk nilai) atau tambah/hapus presensi pada tanggal tertentu.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pilih Karyawan</label>
              <select data-testid="manage-employee-select" value={mngEmp} onChange={(e) => { setMngEmp(e.target.value); setOverrideVal(""); }} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                <option value="">Pilih karyawan</option>
                {employees.map((e) => <option key={e.id} value={e.employee_id}>{e.employee_id} — {e.name}</option>)}
              </select>
              {mngEmp && <p className="mt-2 text-sm text-slate-600">Jumlah presensi saat ini (dipakai untuk nilai): <span className="font-bold text-slate-900" data-testid="current-attendance-count">{counts[mngEmp] ?? 0}</span></p>}
            </div>

            {mngEmp && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Set Angka Total (override)</label>
                <div className="flex gap-2">
                  <input data-testid="override-count-input" type="number" min="0" value={overrideVal} onChange={(e) => setOverrideVal(e.target.value)} placeholder="mis. 5" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                  <button data-testid="save-override-button" onClick={saveOverride} disabled={savingOverride} className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                    {savingOverride ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
                  </button>
                </div>
                <button data-testid="clear-override-button" onClick={() => { setOverrideVal(""); saveOverride(); }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
                  <RotateCcw className="h-3.5 w-3.5" /> Kembalikan ke perhitungan otomatis (dari record)
                </button>
              </div>
            )}
          </div>

          {mngEmp && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="text-sm font-semibold text-slate-700 mb-2">Presensi per Tanggal</p>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <input data-testid="manual-date-input" type="date" min="2026-10-02" max={today} value={manualDate} onChange={(e) => setManualDate(e.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                <button data-testid="add-manual-date-button" onClick={addManualDate} disabled={addingDate} className="flex items-center gap-1.5 rounded-xl bg-[#F16522] px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  {addingDate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Tambah Tanggal
                </button>
              </div>
              {mngRecords.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Belum ada record presensi untuk karyawan ini.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {mngRecords.map((r) => (
                    <span key={r.id} data-testid={`manage-date-${r.id}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                      {r.date}
                      <button data-testid={`manage-delete-${r.id}`} onClick={() => deleteRecord(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
